import { Phone, ExternalLink, Heart, Sparkles, BookOpen, LifeBuoy, MessageCircleHeart } from "lucide-react";

const helplines = [
  {
    name: "Emergency",
    number: "000",
    desc: "Police, ambulance or fire — for immediate, life-threatening situations.",
    tone: "urgent",
    testid: "helpline-000",
  },
  {
    name: "Lifeline",
    number: "13 11 14",
    desc: "24-hour crisis support and suicide prevention. Text 0477 13 11 14.",
    tone: "primary",
    testid: "helpline-lifeline",
  },
  {
    name: "Beyond Blue",
    number: "1300 22 4636",
    desc: "24-hour support for anxiety, depression and general mental wellbeing.",
    tone: "primary",
    testid: "helpline-beyond-blue",
  },
  {
    name: "healthdirect",
    number: "1800 022 222",
    desc: "24-hour nurse-led advice line — for when you're not sure what you need.",
    tone: "secondary",
    testid: "helpline-healthdirect",
  },
  {
    name: "13YARN",
    number: "13 92 76",
    desc: "24-hour crisis support line for Aboriginal and Torres Strait Islander peoples.",
    tone: "secondary",
    testid: "helpline-13yarn",
  },
  {
    name: "1800RESPECT",
    number: "1800 737 732",
    desc: "24-hour national domestic, family and sexual violence counselling service.",
    tone: "secondary",
    testid: "helpline-respect",
  },
];

const organisations = [
  {
    name: "FND Australia Support Services",
    url: "https://fndaus.org.au/",
    desc: "An Australian charity supporting people living with FND — information, peer groups and advocacy.",
    testid: "org-fndaus",
  },
  {
    name: "FND Hope International",
    url: "https://fndhope.org/",
    desc: "Global non-profit with patient guides, a clinician directory, and free bi-monthly Zoom peer support groups — including a dedicated group for carers.",
    testid: "org-fndhope",
  },
  {
    name: "Neurosymptoms.org",
    url: "https://neurosymptoms.org/",
    desc: "Patient-facing site by Prof. Jon Stone — clear explanations of how FND works.",
    testid: "org-neurosymptoms",
  },
  {
    name: "FND Action",
    url: "https://www.fndaction.org.uk/",
    desc: "UK-based FND charity with excellent plain-language patient resources.",
    testid: "org-fndaction",
  },
];

const selfHelp = [
  {
    icon: Sparkles,
    title: "Pace, don't push",
    body: "Many people with FND find a boom-and-bust cycle makes things worse. Small, steady is almost always kinder than all-or-nothing.",
    testid: "self-help-pace",
  },
  {
    icon: Heart,
    title: "Name what's happening",
    body: "Out loud or in writing: \"This is a functional symptom. My body's signals are crossed. It will pass.\" Naming softens panic.",
    testid: "self-help-name",
  },
  {
    icon: LifeBuoy,
    title: "Ground into the senses",
    body: "Five things you see, four you hear, three you feel, two you smell, one you taste. Anchor yourself back into the room.",
    testid: "self-help-ground",
  },
  {
    icon: MessageCircleHeart,
    title: "Find one safe person",
    body: "A friend, family member, GP or support group. Not ten. One person who lets you describe it without flinching.",
    testid: "self-help-person",
  },
];

export default function Resources() {
  return (
    <div data-testid="page-resources">
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            <span className="w-6 h-px bg-terracotta" />
            Gathered with care
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1
              data-testid="resources-title"
              className="lg:col-span-8 font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-balance"
            >
              Resources &
              <span className="italic text-terracotta"> support</span>.
            </h1>
            <p className="lg:col-span-4 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed">
              A small, Australian-first collection — helplines when the night gets long,
              organisations worth bookmarking, and grounding practices from lived experience.
            </p>
          </div>
        </div>
      </section>

      {/* Emergency note */}
      <section className="pb-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div
            data-testid="resources-disclaimer"
            className="rounded-3xl border border-terracotta/30 bg-terracotta/10 dark:bg-terracotta/15 p-6 lg:p-8"
          >
            <p className="text-base text-charcoal dark:text-bone leading-relaxed">
              <strong className="font-semibold">If you are in immediate danger,</strong>{" "}
              call <a href="tel:000" className="underline underline-offset-4 decoration-terracotta hover:text-terracotta transition">000</a> now.
              Nothing on this page replaces emergency care or a clinician who knows you.
            </p>
          </div>
        </div>
      </section>

      {/* Helplines (AU) */}
      <section className="pb-20 lg:pb-28" data-testid="helplines-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
              <span className="w-6 h-px bg-moss" />
              Australian helplines
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              Someone to call, <em className="text-terracotta">any hour</em>.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {helplines.map((h) => {
              const urgent = h.tone === "urgent";
              return (
                <a
                  key={h.name}
                  href={`tel:${h.number.replace(/\s/g, "")}`}
                  data-testid={h.testid}
                  className={`group relative rounded-3xl border p-7 lg:p-8 hover-lift transition-colors ${
                    urgent
                      ? "bg-terracotta text-bone border-terracotta"
                      : "bg-bone dark:bg-charcoal-light/10 border-bone-200 dark:border-charcoal-light/20 text-charcoal dark:text-bone hover:border-terracotta/50"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-2xl ${
                        urgent ? "bg-bone/20 text-bone" : "bg-terracotta/10 text-terracotta"
                      }`}
                    >
                      <Phone size={18} strokeWidth={1.5} />
                    </span>
                    <h3 className={`font-serif text-2xl tracking-tight ${urgent ? "text-bone" : "text-charcoal dark:text-bone"}`}>
                      {h.name}
                    </h3>
                  </div>
                  <p
                    className={`font-serif text-3xl lg:text-4xl tracking-tight mb-3 ${
                      urgent ? "text-bone" : "text-terracotta"
                    }`}
                  >
                    {h.number}
                  </p>
                  <p className={`text-sm leading-relaxed ${urgent ? "text-bone/85" : "text-charcoal-light dark:text-bone/65"}`}>
                    {h.desc}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Organisations */}
      <section
        className="py-20 lg:py-28 bg-bone-100/60 dark:bg-charcoal-light/10"
        data-testid="organisations-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <span className="w-6 h-px bg-terracotta" />
              Organisations worth knowing
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              The people who’ve been <em className="text-terracotta">doing this longer</em> than me.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {organisations.map((o) => (
              <a
                key={o.name}
                href={o.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={o.testid}
                className="group relative rounded-3xl bg-bone dark:bg-charcoal-light/15 border border-bone-200 dark:border-charcoal-light/25 p-7 lg:p-8 hover-lift hover:border-terracotta/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-moss/15 text-moss">
                      <BookOpen size={18} strokeWidth={1.5} />
                    </span>
                    <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
                      {o.name}
                    </h3>
                  </div>
                  <ExternalLink
                    size={18}
                    strokeWidth={1.5}
                    className="text-charcoal-light dark:text-bone/60 group-hover:text-terracotta transition mt-2 shrink-0"
                  />
                </div>
                <p className="mt-5 text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                  {o.desc}
                </p>
                <span className="mt-4 inline-block text-xs uppercase tracking-[0.22em] text-terracotta break-all">
                  {o.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Self-help */}
      <section className="py-20 lg:py-28" data-testid="self-help-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
              <span className="w-6 h-px bg-moss" />
              From experience
            </span>
            <h2 className="font-serif text-charcoal dark:text-bone text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
              Small things that have, <em className="text-terracotta">sometimes</em>, helped.
            </h2>
            <p className="mt-5 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
              Not advice — just notes from living with this. Take what fits, leave the rest.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {selfHelp.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={s.testid}
                  className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-7 hover-lift"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-terracotta/10 text-terracotta mb-5">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-xl text-charcoal dark:text-bone tracking-tight mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section className="pb-24 lg:pb-32" data-testid="resources-closing">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <p className="font-serif italic text-2xl sm:text-3xl text-charcoal dark:text-bone leading-snug text-balance">
            “Asking for help is not a sign the FND won. It’s the quietest form of taking your life back.”
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-charcoal-light dark:text-bone/60">
            A note from the author
          </p>
        </div>
      </section>
    </div>
  );
}
